import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAltRounded as FilterAltRoundedIcon,
  Public as PublicIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  LockOutlined as LockOutlinedIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo, puedeEditarProgramaFacturacion } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpDataGridSx, fpInputSx } from 'components/financepro';
import PrintListado from 'utils/PrintListado';
import DetalleEcf from './DetalleEcf';
import VistaPagoExterior from './VistaPagoExterior';
import FormularioPagoExterior from './FormularioPagoExterior';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

/** Clave del programa para validar permisos (misma regla que FacturaPOS). */
export const PROGRAMA_PAGOS_ELECTRONICOS = 'pagoselectronicos';

/** Solo e-CF E47 (Pagos al Exterior). */
const CODIGOS_DOCUMENTO_PAGOS = ['47'];

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** RNC/cédula DGII válido (9 u 11 dígitos). */
const rncCompradorValido = (valor) => {
  const digits = String(valor || '').replace(/\D/g, '');
  if (!digits || digits === '999999') return '';
  if (digits.length === 9 || digits.length === 11) return digits;
  return '';
};

const rncDesdeCompras = (compras) => {
  for (const c of compras || []) {
    const rnc = rncCompradorValido(c.rnc);
    if (rnc) return rnc;
  }
  return '';
};

const nombreSuplidor = (row) =>
  String(row?.suplidor_nombre || '').trim() || String(row?.suplidor || '').trim() || '';

const compraFirmada = (row) => {
  const ncf = String(row?.comprobantefiscal || row?.ncf || '').trim();
  const marcado = String(row?.marcado_electronico || '').trim().toUpperCase() === 'S';
  return marcado || ncf.toUpperCase().startsWith('E');
};

/**
 * Resume compras ya firmadas en una fila por e-CF.
 */
const resumirComprasPorEcf = (list) => {
  const porEcf = new Map();
  for (const r of list || []) {
    const ncf = String(r.comprobantefiscal || r.ncf || '').trim();
    if (!ncf || !compraFirmada(r)) continue;
    if (!porEcf.has(ncf)) {
      porEcf.set(ncf, {
        ...r,
        id: `ecf-${ncf}`,
        numero: r.numero || r.id,
        id_compra: r.numero || r.id,
        ids: [r.numero || r.id],
        cantidad_compras: 1,
        total: toNumber(r.total),
        itbis: toNumber(r.itbis),
        retencion: toNumber(r.retencion),
        descripciones: [String(r.descripcion || '').trim()].filter(Boolean),
        comprasDetalle: [r],
        esResumenEcf: true,
      });
    } else {
      const g = porEcf.get(ncf);
      g.ids.push(r.numero || r.id);
      g.cantidad_compras += 1;
      g.total += toNumber(r.total);
      g.itbis += toNumber(r.itbis);
      g.retencion += toNumber(r.retencion);
      g.comprasDetalle.push(r);
      const desc = String(r.descripcion || '').trim();
      if (desc) g.descripciones.push(desc);
    }
  }

  const vistos = new Set();
  const salida = [];
  for (const r of list || []) {
    const ncf = String(r.comprobantefiscal || r.ncf || '').trim();
    if (!ncf || !compraFirmada(r)) {
      salida.push({
        ...r,
        id: r.numero || r.id,
        cantidad_compras: 1,
        esResumenEcf: false,
        comprasDetalle: [r],
      });
      continue;
    }
    if (vistos.has(ncf)) continue;
    vistos.add(ncf);
    const g = porEcf.get(ncf);
    const preview = g.descripciones.slice(0, 2).join('; ');
    salida.push({
      ...g,
      descripcion:
        g.cantidad_compras === 1
          ? g.descripciones[0] || g.descripcion || ''
          : `${g.cantidad_compras} pagos${preview ? `: ${preview}` : ''}${
              g.descripciones.length > 2 ? '…' : ''
            }`,
    });
  }
  return salida;
};

/** Convierte YYYY-MM-DD / ISO / Date a dd/mm/aaaa. */
const formatFecha = (fecha) => {
  if (fecha == null || fecha === '') return '';
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    const d = String(fecha.getDate()).padStart(2, '0');
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const y = fecha.getFullYear();
    return `${d}/${m}/${y}`;
  }
  const s = String(fecha).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmyDash = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyDash) return `${dmyDash[1]}/${dmyDash[2]}/${dmyDash[3]}`;
  if (s === '%Y-%m-%d' || s === '%%Y-%%m-%%d') return '';
  return s;
};

const PagosElectronicos = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    roleDetalles,
    nombreNegocio,
    rnc_empresa,
    direccion_empresa,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () =>
      tienePermisosModulo(roleDetalles, PROGRAMA_PAGOS_ELECTRONICOS) ||
      tienePermisosModulo(roleDetalles, 'desembolso') ||
      tienePermisosModulo(roleDetalles, 'facturacion'),
    [roleDetalles]
  );

  const puedeModificar = useMemo(
    () =>
      puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_PAGOS_ELECTRONICOS) ||
      tienePermisosModulo(roleDetalles, 'desembolso'),
    [roleDetalles]
  );

  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(hoy.slice(0, 8) + '01');
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [concepto, setConcepto] = useState('');
  const [suplidor, setSuplidor] = useState('');
  const [filtroEcf, setFiltroEcf] = useState('firmados');

  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [tiposDocumentosFiscales, setTiposDocumentosFiscales] = useState([]);
  const [tipoDocumento, setTipoDocumento] = useState('');

  const [vistaOpen, setVistaOpen] = useState(false);
  const [vistaData, setVistaData] = useState({
    compras: [],
    ncf: '',
    emisionData: {},
    rncComprador: '',
    razonSocialComprador: '',
  });
  const [detalleEcfAbierto, setDetalleEcfAbierto] = useState(false);
  const [compraDetalleEcf, setCompraDetalleEcf] = useState(null);
  const [reimprimiendoNcf, setReimprimiendoNcf] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  const rowsGrid = useMemo(() => resumirComprasPorEcf(rows), [rows]);

  const totalLista = useMemo(
    () => rowsGrid.reduce((sum, g) => sum + toNumber(g.total), 0),
    [    rowsGrid]
  );

  const cargarTiposDocumentosFiscales = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(`${servidor}/api/facturabackend_listartiposdocumentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const tiposActivos = (data.tipos || []).filter(
          (tipo) =>
            tipo.activo === 'S' &&
            CODIGOS_DOCUMENTO_PAGOS.includes(codigoTipoDocumento(tipo))
        );
        setTiposDocumentosFiscales(tiposActivos);
        if (tiposActivos.length > 0) {
          setTipoDocumento((prev) => prev || tiposActivos[0].nombre);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar tipos de documentos fiscales');
      }
    } catch (_e) {
      showError('Error de conexión al cargar tipos de documentos fiscales');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!puedeLeer) return;
    cargarTiposDocumentosFiscales();
  }, [puedeLeer, cargarTiposDocumentosFiscales]);

  const buscarCompras = useCallback(async () => {
    if (!puedeLeer) {
      showError('No tiene permisos para consultar pagos al exterior.');
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      showError('Seleccione un rango de fechas');
      return;
    }
    if (fechaDesde > fechaHasta) {
      showError('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setCargando(true);
    try {
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_listarpagoselectronicos`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            fechadesde: fechaDesde,
            fechahasta: fechaHasta,
            filtro_ecf: filtroEcf,
            tipo_ecf: '47',
            concepto: concepto.trim() || undefined,
            suplidor: suplidor.trim() || undefined,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        const list = (data.data || []).map((r) => ({
          ...r,
          id: r.numero || r.id,
          fecha: formatFecha(r.fecha),
          fecha_ecf: formatFecha(r.fecha_ecf || r.fecha_firma_ecf || ''),
          total: toNumber(r.total),
          itbis: toNumber(r.itbis),
          retencion: toNumber(r.retencion),
        }));
        setRows(list);
        if (list.length === 0) {
          showSuccess('No se encontraron pagos con los filtros aplicados');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setRows([]);
        showError(errorData.Error || 'No se pudieron cargar los pagos al exterior');
      }
    } catch (_e) {
      setRows([]);
      showError('Error de conexión al cargar pagos al exterior');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    fechaDesde,
    fechaHasta,
    filtroEcf,
    concepto,
    suplidor,
    servidor,
    usuarioLog,
    claveLog,
    showError,
    showSuccess,
  ]);

  useEffect(() => {
    if (!puedeLeer || !fechaDesde || !fechaHasta) return undefined;
    buscarCompras();
    return undefined;
    // Recarga al cambiar el filtro de firma o al crear un desembolso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEcf, refreshNonce]);

  const handleLimpiarFiltros = () => {
    setFechaDesde(hoy.slice(0, 8) + '01');
    setFechaHasta(hoy);
    setConcepto('');
    setSuplidor('');
    setFiltroEcf('firmados');
  };

  const abrirDetalleFirma = useCallback(
    (row) => {
      const ncf = String(row?.comprobantefiscal || row?.ncf || '').trim();
      if (!ncf) {
        showError('Este pago no tiene e-CF para consultar la firma.');
        return;
      }
      setCompraDetalleEcf({
        ...row,
        encf: ncf,
        ncf,
        nfact: row.id_compra || row.ids?.[0] || row.numero || row.id,
        tipofactura: 'Pagos al Exterior',
        nombre_cliente: nombreSuplidor(row) || row.descripcion || '',
        rnc_comprador: rncCompradorValido(row.rnc),
        monto_total: toNumber(row.total) + toNumber(row.itbis),
        fecha_factura: row.fecha,
        estado: row.estado_ecf,
        fecha_firma: row.fecha_firma_ecf || '',
      });
      setDetalleEcfAbierto(true);
    },
    [showError]
  );

  const reimprimirCompra = useCallback(
    async (row) => {
      const ncf = String(row?.comprobantefiscal || row?.ncf || '').trim();
      if (!ncf) {
        showError('Este pago no tiene e-CF para reimprimir.');
        return;
      }
      if (!usuarioLog) {
        showError('Sesión inválida: falta usuario de login');
        return;
      }
      setReimprimiendoNcf(ncf);
      try {
        const response = await fetch(
          `${servidor}/api/facturaselectronicasdgii_obtenercompraelectronica`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              comprobante: ncf,
            }),
          }
        );
        const data = await response.json().catch(() => ({}));
        const detalleLocal =
          Array.isArray(row?.comprasDetalle) && row.comprasDetalle.length
            ? row.comprasDetalle
            : [row].filter((g) => !g.esResumenEcf);
        if (!response.ok && detalleLocal.length === 0) {
          showError(data.Error || 'No se pudo cargar el pago al exterior');
          return;
        }
        const emision = data.emision || {};
        const rncDigits = String(emision.rnc_comprador || row.rnc || '').replace(/\D/g, '');
        const rncComprador =
          rncDigits &&
          rncDigits !== '999999' &&
          (rncDigits.length === 9 || rncDigits.length === 11)
            ? rncDigits
            : '';
        const comprasImpresion =
          Array.isArray(data.compras) && data.compras.length > 0 ? data.compras : detalleLocal;
        if (!comprasImpresion.length) {
          showError('No hay detalle de pagos para reimprimir este e-CF');
          return;
        }
        setVistaData({
          compras: comprasImpresion,
          ncf: data.ncf || ncf,
          emisionData: {
            ...emision,
            codigo_seguridad: emision.codigo_seguridad || '',
            fecha_firma: emision.fecha_firma || row.fecha_firma_ecf || '',
            estado: emision.estado || row.estado_ecf || '',
            encf: emision.encf || ncf,
          },
          rncComprador,
          razonSocialComprador:
            emision.razon_social_comprador || nombreSuplidor(row) || nombreSuplidor(comprasImpresion[0]),
        });
        setVistaOpen(true);
      } catch (_e) {
        const detalleLocal =
          Array.isArray(row?.comprasDetalle) && row.comprasDetalle.length ? row.comprasDetalle : [];
        if (detalleLocal.length > 0) {
          setVistaData({
            compras: detalleLocal,
            ncf,
            emisionData: {
              codigo_seguridad: '',
              fecha_firma: row.fecha_firma_ecf || '',
              estado: row.estado_ecf || '',
              encf: ncf,
            },
            rncComprador: rncDesdeCompras(detalleLocal),
            razonSocialComprador: nombreSuplidor(row),
          });
          setVistaOpen(true);
        } else {
          showError('Error de conexión al reimprimir el pago al exterior');
        }
      } finally {
        setReimprimiendoNcf('');
      }
    },
    [usuarioLog, claveLog, servidor, showError]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'numero',
        headerName: 'Nº',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const nro = params.row?.id_compra || params.row?.numero || params.value;
          return nro == null || nro === '' ? '—' : nro;
        },
      },
      {
        field: 'fecha',
        headerName: 'Fecha',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatFecha(value),
      },
      {
        field: 'fecha_ecf',
        headerName: 'Fecha firma',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value, row) => formatFecha(row?.fecha_ecf || row?.fecha_firma_ecf || ''),
      },
      {
        field: 'suplidor_nombre',
        headerName: 'Suplidor',
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => nombreSuplidor(row) || '—',
      },
      {
        field: 'rnc',
        headerName: 'ID / RNC',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => rncCompradorValido(value) || String(value || '').trim() || '—',
      },
      {
        field: 'descripcion',
        headerName: 'Concepto',
        flex: 1.4,
        minWidth: 200,
      },
      {
        field: 'total',
        headerName: 'Subtotal',
        type: 'number',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `$${formatMoney(value)}`,
      },
      {
        field: 'retencion',
        headerName: 'ISR 27%',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `$${formatMoney(value)}`,
      },
      {
        field: 'comprobantefiscal',
        headerName: 'e-CF',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const ncf = String(
            params.row?.comprobantefiscal || params.row?.ncf || params.value || ''
          ).trim();
          return ncf || '—';
        },
      },
      {
        field: 'estado_ecf',
        headerName: 'Estado',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const marcado = compraFirmada(params.row);
          const estado = String(params.row?.estado_ecf || params.value || '').trim().toLowerCase();
          if (!estado && !marcado) return '—';
          const colorMap = {
            aceptado: 'success',
            aceptado_condicional: 'warning',
            rechazado: 'error',
            en_proceso: 'info',
            pendiente_envio: 'default',
            firmado: 'info',
            error: 'error',
          };
          const labelMap = {
            aceptado: 'Aceptado',
            aceptado_condicional: 'Condicional',
            rechazado: 'Rechazado',
            en_proceso: 'En proceso',
            pendiente_envio: 'Pendiente envío',
            firmado: 'Firmado',
            error: 'Error',
          };
          const clave = estado || 'firmado';
          return (
            <Chip
              size="small"
              label={labelMap[clave] || clave}
              color={colorMap[clave] || 'default'}
              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
            />
          );
        },
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const ncf = String(params.row?.comprobantefiscal || params.row?.ncf || '').trim();
          const firmado = compraFirmada(params.row);
          const reimprimiendo = reimprimiendoNcf && reimprimiendoNcf === ncf;
          return (
            <Stack direction="row" spacing={0.25} justifyContent="center">
              <Tooltip title={ncf ? 'Ver datos de la firma' : 'Sin e-CF firmado'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!ncf || !usuarioLog}
                    onClick={() => abrirDetalleFirma(params.row)}
                    sx={{
                      color: FP.color.navy,
                      '&:hover': { bgcolor: FP.color.surfaceAlt },
                      '&.Mui-disabled': { color: FP.color.textMuted },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={ncf && firmado ? 'Reimprimir pago al exterior' : 'Sin e-CF para reimprimir'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!ncf || !firmado || !usuarioLog || !!reimprimiendoNcf}
                    onClick={() => reimprimirCompra(params.row)}
                    sx={{
                      color: FP.color.accentBlue,
                      '&:hover': { bgcolor: FP.color.accentBlueSoft },
                      '&.Mui-disabled': { color: FP.color.textMuted },
                    }}
                  >
                    {reimprimiendo ? (
                      <CircularProgress size={16} sx={{ color: FP.color.accentBlue }} />
                    ) : (
                      <PrintIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ];
    return filtroEcf === 'pendientes' ? cols.filter((c) => c.field !== 'fecha_ecf') : cols;
  }, [reimprimiendoNcf, usuarioLog, abrirDetalleFirma, reimprimirCompra, filtroEcf]);

  const columnasPrint = useMemo(
    () => [
      { id: 'numero', label: 'Nº', align: 'center', render: (_, row) => row.id_compra || row.numero || '—' },
      { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatFecha(row.fecha) || '—' },
      { id: 'suplidor_nombre', label: 'Suplidor', render: (_, row) => nombreSuplidor(row) || '—' },
      { id: 'rnc', label: 'ID / RNC', render: (_, row) => rncCompradorValido(row.rnc) || row.rnc || '—' },
      { id: 'descripcion', label: 'Concepto', render: (v) => v || '—' },
      {
        id: 'total',
        label: 'Subtotal',
        align: 'right',
        render: (v) => `$${formatMoney(v)}`,
      },
      { id: 'retencion', label: 'ISR 27%', align: 'right', render: (v) => `$${formatMoney(v)}` },
      { id: 'comprobantefiscal', label: 'e-CF', align: 'center', render: (_, row) => row.comprobantefiscal || row.ncf || '—' },
      {
        id: 'estado_ecf',
        label: 'Estado',
        align: 'center',
        render: (_, row) => {
          const estado = String(row.estado_ecf || '').toLowerCase();
          if (!estado && !compraFirmada(row)) return '—';
          return estado ? estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ') : 'Firmado';
        },
      },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Pagos al exterior"
          subtitle={emisorFiscal.nombre ? `${emisorFiscal.nombre} · e-CF E47` : 'e-CF E47'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «pagoselectronicos» en roles para acceder a esta pantalla.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Pagos al exterior"
        subtitle={
          emisorFiscal.nombre
            ? `${emisorFiscal.nombre} · Emisión e-CF E47 (desembolsos)`
            : 'Emisión e-CF E47 (desembolsos)'
        }
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
              disabled={!puedeModificar}
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                px: 2.2,
                height: 38,
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              Nuevo pago
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              label={filtroEcf === 'firmados' ? 'e-CF en lista' : 'Pagos en lista'}
              value={String(rowsGrid.length)}
              icon={ReceiptIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              label="Total"
              value={`$${formatMoney(totalLista)}`}
              icon={PublicIcon}
              accent={FP.color.navy}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Typography
              sx={{
                ml: 'auto',
                color: FP.color.textSecondary,
                fontSize: '0.78rem',
                fontWeight: 600,
              }}
            >
              Desembolsos · E47 ·{' '}
              {filtroEcf === 'firmados' ? 'Firmados' : filtroEcf === 'todos' ? 'Todos' : 'Pendientes'}
            </Typography>
            <Box className="no-print">
              <PrintListado
                title={emisorFiscal.nombre || 'Empresa'}
                empresa={{
                  nombre: emisorFiscal.nombre || '',
                  direccion: emisorFiscal.direccion || '',
                  rnc: emisorFiscal.rnc || '',
                }}
                reportTitle="Pagos al exterior"
                subtitle={`Del ${fechaDesde || '...'} al ${fechaHasta || '...'}`}
                columns={columnasPrint}
                data={rowsGrid}
                emptyMessage="No hay pagos para imprimir"
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
                footer={`Total pagos: ${rowsGrid.length} | Suma: $${formatMoney(totalLista)}`}
              />
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label={filtroEcf === 'firmados' ? 'Firma desde' : 'Fecha desde'}
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={filtroEcf === 'firmados' ? 'Filtra por fecha de firma del e-CF' : ' '}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label={filtroEcf === 'firmados' ? 'Firma hasta' : 'Fecha hasta'}
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText={filtroEcf === 'firmados' ? 'Filtra por fecha de firma del e-CF' : ' '}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Filtro e-CF"
                  value={filtroEcf}
                  onChange={(e) => setFiltroEcf(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="pendientes">Sin e-CF</MenuItem>
                  <MenuItem value="firmados">Ya firmados</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Concepto"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  placeholder="Buscar en descripción"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Suplidor"
                  value={suplidor}
                  onChange={(e) => setSuplidor(e.target.value)}
                  placeholder="Nombre, código o RNC"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    onClick={buscarCompras}
                    disabled={cargando || !usuarioLog}
                    sx={{
                      height: 40,
                      px: 3,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      boxShadow: 'none',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 40,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {tiposDocumentosFiscales.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No hay un tipo de documento fiscal E47 (Pagos al Exterior) activo. Configúrelo en tipos de
                documentos fiscales.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              {filtroEcf === 'firmados'
                ? 'e-CF firmados (resumen)'
                : filtroEcf === 'todos'
                  ? 'Todos los desembolsos'
                  : 'Pagos sin e-CF'}
            </Typography>
            <Typography
              sx={{
                ml: 'auto',
                color: FP.color.textSecondary,
                fontSize: '0.85rem',
                fontWeight: 700,
              }}
            >
              Total: ${formatMoney(totalLista)}
            </Typography>
          </Box>
          <Box sx={{ height: 480, width: '100%', p: { xs: 1.5, md: 2 } }}>
            <DataGrid
              rows={rowsGrid}
              columns={columns}
              checkboxSelection={false}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              loading={cargando}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              rowHeight={44}
              sx={fpDataGridSx}
            />
          </Box>
        </Card>
      </Box>

      <FormularioPagoExterior
        open={formOpen}
        onClose={() => setFormOpen(false)}
        puedeModificar={puedeModificar}
        tiposDocumentosFiscales={tiposDocumentosFiscales}
        tipoDocumento={tipoDocumento}
        onTipoDocumentoChange={setTipoDocumento}
        onCreada={(resultado) => {
          cargarTiposDocumentosFiscales();
          if (!resultado?.ncf || resultado.fallido) {
            setRefreshNonce((n) => n + 1);
            return;
          }
          setVistaData(resultado);
          setVistaOpen(true);
          setConcepto('');
          setSuplidor('');
          setFechaDesde(hoy.slice(0, 8) + '01');
          setFechaHasta(hoy);
          setFiltroEcf('firmados');
          setRefreshNonce((n) => n + 1);
        }}
      />

      <VistaPagoExterior
        open={vistaOpen}
        onClose={() => setVistaOpen(false)}
        compras={vistaData.compras}
        ncf={vistaData.ncf}
        emisionData={vistaData.emisionData}
        rncComprador={vistaData.rncComprador}
        razonSocialComprador={vistaData.razonSocialComprador}
        empresa={{
          nombre: emisorFiscal.nombre || '',
          direccion: emisorFiscal.direccion || '',
          rnc: emisorFiscal.rnc || '',
        }}
      />

      <DetalleEcf
        open={detalleEcfAbierto}
        onClose={() => {
          setDetalleEcfAbierto(false);
          setCompraDetalleEcf(null);
        }}
        encf={compraDetalleEcf?.ncf || ''}
        factura={compraDetalleEcf}
        onEstadoActualizado={(actualizado) => {
          const encfActualizado = actualizado?.encf;
          if (!encfActualizado) return;
          const estadoNuevo = String(actualizado.estado || '').trim().toLowerCase();
          setRows((prev) =>
            prev.map((item) =>
              item.comprobantefiscal === encfActualizado || item.ncf === encfActualizado
                ? { ...item, estado_ecf: actualizado.estado || item.estado_ecf }
                : item
            )
          );
          setCompraDetalleEcf((prev) =>
            prev && prev.ncf === encfActualizado
              ? { ...prev, estado: actualizado.estado || prev.estado, estado_ecf: actualizado.estado }
              : prev
          );
          if (estadoNuevo !== 'rechazado' && estadoNuevo !== 'error') return;
          const numero = parseInt(
            String(
              compraDetalleEcf?.nfact ||
                compraDetalleEcf?.id_compra ||
                compraDetalleEcf?.numero ||
                ''
            ),
            10
          );
          (async () => {
            try {
              await fetch(`${servidor}/api/facturaselectronicasdgii_actualizarestadoecf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  usuario: usuarioLog || '',
                  password: claveLog || '', empresa: empresaFiscal,
                  comprobante: encfActualizado,
                  estado: estadoNuevo,
                }),
              });
              if (numero > 0) {
                await fetch(`${servidor}/api/facturaselectronicasdgii_revertircompraelectronica`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    usuario: usuarioLog || '',
                    password: claveLog || '', empresa: empresaFiscal,
                    numero,
                    comprobante: encfActualizado,
                  }),
                });
              }
            } catch (_ignored) {
              /* best-effort: la secuencia ya se consume en el backend */
            }
            cargarTiposDocumentosFiscales();
            setRefreshNonce((n) => n + 1);
          })();
        }}
      />
    </Box>
  );
};

export default PagosElectronicos;
